import { ConditionsGroup } from "@/services/segments/filters";
import { FilterForm, SegmentsParams } from "@/store/segments/segments";
import { formatDateToYYYYMMDD } from "../utils/DateParser";

// Интерфейс для элемента справочника возраста
export interface AgeItem {
    id: number;
    type: string;
}

// Структура маппингов для работы с возрастами в фильтрах
export interface AgeMappings {
    typeToId: Record<string, number>;
    idToType: Record<number, string>;
}

// Утилита для создания маппингов из списка возрастов
export function createAgeMappings(agesList: AgeItem[]): AgeMappings {
    const typeToId: Record<string, number> = {};
    const idToType: Record<number, string> = {};

    agesList.forEach(age => {
        typeToId[age.type] = age.id;
        idToType[age.id] = age.type;
    });

    return { typeToId, idToType };
}

// Утилита для конвертации числовых id age в type для отображения в форме
export function convertAgeIdsToTypes(ageValues: any[], ageMappings?: AgeMappings): string[] {
    if (!ageMappings) return ageValues.map(v => v.toString());

    return ageValues.map((val: any) => {
        if (typeof val === "number") {
            return ageMappings.idToType[val] ?? val.toString();
        }
        return val;
    });
}

// Утилита для переключения значения возраста
export function toggleAgeValue(ageArray: number[], value: number): number[] {
    const index = ageArray.indexOf(value);
    if (index > -1) {
        const newArray = [...ageArray];
        newArray.splice(index, 1);
        return newArray;
    }
    return [...ageArray, value];
}

// Константы для пола
export const GENDER_VALUES = {
    MALE: 0,
    FEMALE: 1,
} as const;

export const GENDER_NAMES = {
    MALE: "male",
    FEMALE: "female",
} as const;


// Утилиты для работы с полом
export function convertGenderValuesToNames(genderValues: number[]): string[] {
    return genderValues.map(val => {
        if (val === GENDER_VALUES.MALE) return GENDER_NAMES.MALE;
        if (val === GENDER_VALUES.FEMALE) return GENDER_NAMES.FEMALE;
        return val.toString();
    });
}

export function toggleGenderValue(genderArray: number[], value: number): number[] {
    const index = genderArray.indexOf(value);
    if (index > -1) {
        const newArray = [...genderArray];
        newArray.splice(index, 1);
        return newArray;
    }
    return [...genderArray, value];
}

export function formatGenderDisplay(genderValues: number[]): string {
    if (!genderValues || genderValues.length === 0) {
        return "";
    }

    const genderLabels = genderValues.map(val => {
        if (val === GENDER_VALUES.MALE) return "М";
        if (val === GENDER_VALUES.FEMALE) return "Ж";
        return "";
    }).filter(label => label !== "");

    return genderLabels.join(", ");
}


export function hasModifiedConditions(
    conditionsGroups: ConditionsGroup[]
): boolean {
    return conditionsGroups.some((conditionGroup) => {
        const isPeriodStartModified = conditionGroup.period.start !== null || conditionGroup.flexible_period.start.interval != 0;
        // const isPeriodEndModified = conditionGroup.period.end !== null;

        const isCategoryModified = Object.keys(conditionGroup.category).length > 0;
        const isBrandModified = conditionGroup.brand.length > 0;
        const isShopModified = conditionGroup.shop.length > 0;
        const isGenderModified = conditionGroup.gender.length > 0;
        const isAgeModified = conditionGroup.age.length > 0;
        const isTotalModified =
            (conditionGroup.total != null &&
                typeof conditionGroup.total === "number" &&
                Number.isFinite(conditionGroup.total)) ||
            (conditionGroup.maxTotal != null &&
                typeof conditionGroup.maxTotal === "number" &&
                Number.isFinite(conditionGroup.maxTotal));
        const isMinPurchasesModified =
            conditionGroup.minPurchasesCount != null &&
            Number.isInteger(conditionGroup.minPurchasesCount) &&
            conditionGroup.minPurchasesCount >= 2;
        const isMaxPurchasesModified =
            conditionGroup.maxPurchasesCount != null &&
            Number.isInteger(conditionGroup.maxPurchasesCount) &&
            conditionGroup.maxPurchasesCount >= 2;


        return (
            isPeriodStartModified &&
            isGenderModified &&
            isAgeModified &&
            (isCategoryModified || isBrandModified || isShopModified || isTotalModified || isMinPurchasesModified || isMaxPurchasesModified)
        );
    });
}

export function validatePurchasesBounds(group: ConditionsGroup): string | null {
    const min = group.minPurchasesCount;
    const max = group.maxPurchasesCount;
    if (min != null && (!Number.isInteger(min) || min < 2))
        return "Кол-во чеков «от»: не меньше 2";
    if (max != null && (!Number.isInteger(max) || max < 2))
        return "Кол-во чеков «до»: не меньше 2";
    if (min != null && max != null && min > max)
        return "Кол-во чеков «от» не может быть больше «до»";
    return null;
}

export function validateTotalRange(group: ConditionsGroup): string | null {
    const min = group.total;
    const max = group.maxTotal;
    if (min != null && max != null && Number(min) > Number(max))
        return "Сумма чека «от» не может быть больше «до»";
    return null;
}

export function validateSegmentBounds(group: ConditionsGroup): string | null {
    return validatePurchasesBounds(group) ?? validateTotalRange(group);
}

const DEFAULT_COUNT_DISABLED_REASON =
    "Необходимо указать условия выборки (категория и(или) бренд и(или) магазин и(или) сумма чека и(или) число чеков), а также период, пол и возраст.";

export function getCountDisabledReason(
    conditionsGroups: ConditionsGroup[]
): string | null {
    const group = conditionsGroups[0];
    if (!group) return DEFAULT_COUNT_DISABLED_REASON;

    const boundsError = validateSegmentBounds(group);
    if (boundsError) return boundsError;

    if (!hasModifiedConditions(conditionsGroups)) {
        return DEFAULT_COUNT_DISABLED_REASON;
    }

    return null;
}

export function setParamsByFilter(
    params: SegmentsParams,
    settings: TableSettings
) {
    if (settings.stateId) {
        params.state_id = settings.stateId;
    } else {
        params.state_id__in = settings.stateIdIn;
    }

    if (settings.orderField) {
        params.order_by = settings.orderField;
    }

    if (Object.keys(settings.filter).length > 0) {
        if (settings.filter.name) {
            params.name__ilike = settings.filter.name;
        }

        if (settings.filter.state.length > 0) {
            params.state_id__in = settings.filter.state.join(",");
        }

        if (settings.filter.type.length == 1) {
            params.type_id = settings.filter.type[0];
        }

        if (settings.filter.brand.length > 0) {
            params.brands__brand_pk__in = settings.filter.brand.join(",");
        }

        if (settings.filter.shop.length > 0) {
            params.shops__shop_id__in = settings.filter.shop.join(",");
        }

        if (settings.filter.user.length > 0) {
            params.cuser__id__in = settings.filter.user.join(",");
        }

        if (Object.keys(settings.filter.category).length > 0) {
            params.categories__category_id__in = getCategories(
                settings.filter.category
            ).join(",");
        }

        if (settings.filter.date)
            params.cdate = formatDateToYYYYMMDD(settings.filter.date);

        if (settings.filter.gender && settings.filter.gender.length > 0) {
            params.genders__gender_id__in
                = settings.filter.gender.join(",");
        }

        if (settings.filter.age && settings.filter.age.length > 0) {
            params.ages__age_id__in = settings.filter.age.join(",");
        }
    }

    if (settings.commonFilter) {
        params.name__ilike = settings.commonFilter;
    }
}



function getCategories(category: any) {
    const values: number[] = [];
    for (const key in category) {
        const parentKeyParts = key.split("-");
        const catId = parentKeyParts.pop();
        const parentKey = parentKeyParts.join("-");

        if (
            catId != "0" &&
            category[key].checked &&
            !(category[parentKey] && category[parentKey].checked)
        ) {
            if (catId) values.push(parseInt(catId));
        }
    }

    return values;
}

interface TableSettings {
    filter: FilterForm;
    first: number;
    orderField: string;
    stateId: number;
    stateIdIn: string;
    commonFilter: string;
}
